<template>
  <!--
    사업장 「주문 미수락 자동 취소」 설정 패널 — **자기 경로로 읽고 자기 버튼으로 저장하는** 독립 패널이다.
    사업장 폼(18칸 merge 저장)에 얹지 않는다: 두 값은 전용 경로 `GET|PUT /cust/{custCd}/accept-timeout` 로만 바뀐다
    (준비 문서 §3-1). 사업장 정보 관리(사업장 계정)와 사업장 트리(내부 계정)가 같은 이 패널을 쓴다.

    경로·액션·번역·문구는 `utils/orderAcceptPolicy` 한 곳이고, 이 패널은 요청하고 그린다.
    권한은 세션 **액션**만 본다(역할 이름 없음) — 읽기 액션이 없는 세션에는 패널이 없고 요청도 나가지 않는다.
  -->
  <el-card v-if="canView" v-loading="loading" shadow="never" class="order-accept-policy">
    <div slot="header" class="order-accept-policy__header">{{ $t(text.title) }}</div>

    <p class="form-field-hint order-accept-policy__intro">{{ $t(text.intro) }}</p>

    <el-alert v-if="unavailable" type="info" :title="unavailable" :closable="false" show-icon />

    <el-form
      v-else-if="view"
      :model="form"
      :label-width="$device.mobile ? `` : `140px`"
      :label-position="$device.mobile ? `top` : ``"
      label-suffix=":"
      size="small"
      :disabled="!canSave || saving"
      @submit.native.prevent>
      <el-form-item :label="$t(text.minutesLabel)" :error="formError.minutes">
        <!--
          `v-model` 이 아니라 `:value`+`@input` — 비움(`null`)의 번역 자리를 하나로 둔다(`minutesInputValue`).
          `@input` 인 이유(리뷰 X-a): 칸이 정리한 값(정수 `:precision="0"`·범위)은 `input` 으로만 온다 — `change` 만 들으면 `2.5` 를
          넣었을 때 화면은 `3` 인데 폼(저장 본문)은 `2.5` 였다(서버 400).
        -->
        <el-input-number
          :value="minutesInput"
          :min="minutesRange.MIN"
          :max="minutesRange.MAX"
          :step="1"
          :precision="0"
          step-strictly
          :controls="false"
          :placeholder="$t(text.inherit)"
          class="order-accept-policy__minutes"
          @input="onMinutes"></el-input-number>
        <span class="order-accept-policy__suffix">{{ $t(text.minutesUnit) }}</span>
        <div class="form-field-hint">{{ view.minutesCurrent }}</div>
        <div class="form-field-hint">{{ view.minutesHint }}</div>
      </el-form-item>

      <el-form-item :label="$t(text.targetsLabel)" :error="formError.targets">
        <!--
          「상위 설정 따름」 체크를 **여기서는** 쓴다 — 분 칸은 비움으로 `null` 을 말할 수 있지만(placeholder 가 그 자리를
          설명한다), 체크박스 묶음은 「아무것도 안 고름」이 이미 `[]`(대상 없음)이다. 한 묶음으로 `null` 과 `[]` 를 가를 수
          없으므로 축을 하나 더 둔다. 이 체크는 따로 저장하지 않고 `form.targets` 에서 파생한다.
        -->
        <el-checkbox v-model="inheritTargets">{{ $t(text.inherit) }}</el-checkbox>
        <el-checkbox-group v-if="!inheritTargets" v-model="targets" class="order-accept-policy__targets">
          <el-checkbox v-for="token in view.allTargets" :key="token" :label="token">
            {{ $t(targetLabel(token)) }}
            <span v-if="targetHint(token)" class="order-accept-policy__target-hint">{{ $t(targetHint(token)) }}</span>
          </el-checkbox>
        </el-checkbox-group>
        <div class="form-field-hint">{{ view.targetsCurrent }}</div>
        <div class="form-field-hint">{{ view.targetsHint }}</div>
      </el-form-item>

      <!-- 서버 문장 그대로(토스트 대신 여기서 한 번) — 다음 저장이나 사업장 전환이 지운다 -->
      <el-alert
        v-if="errorMessage"
        class="order-accept-policy__error"
        type="error"
        :title="errorMessage"
        :closable="false"
        show-icon />

      <div v-if="canSave" class="order-accept-policy__actions">
        <el-button type="primary" size="small" icon="el-icon-check" :loading="saving" @click="save">{{
          $t(text.save)
        }}</el-button>
      </div>
    </el-form>
  </el-card>
</template>

<script>
import { mergeErrors } from '@/plugins/http'
import { formFailureOf } from '@/plugins/http/policy'
import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { latestGuard } from '@/utils/latestGuard'
import {
  ORDER_ACCEPT_POLICY_ACTION,
  ORDER_ACCEPT_POLICY_MINUTES,
  ORDER_ACCEPT_POLICY_SAVE_CONFIG,
  ORDER_ACCEPT_POLICY_TEXT,
  minutesFromInput,
  minutesInputValue,
  orderAcceptPolicyErrorKeys,
  orderAcceptPolicyRead,
  orderAcceptPolicyUrl,
  orderAcceptPolicyView,
  targetHint,
  targetLabel,
  toPolicyForm,
  toPolicyPayload,
} from '@/utils/orderAcceptPolicy'

export default {
  name: 'CustOrderAcceptPolicyPanel',

  props: {
    /** 대상 사업장 — 바뀌면 다시 읽고, 나가 있던 앞 사업장의 응답은 버린다 */
    custCd: { type: Number, required: true },
  },

  data() {
    return {
      text: ORDER_ACCEPT_POLICY_TEXT,
      minutesRange: ORDER_ACCEPT_POLICY_MINUTES,
      /** 서버가 마지막으로 준 상태의 말(출처·현재 적용·전체 기본값) — 읽기 전·못 읽었으면 `null` */
      view: null,
      /** 편집 중인 값 — `{minutes, targets}`, 각각 `null` = 상위에서 물려받음 */
      form: toPolicyForm(null),
      formError: orderAcceptPolicyErrorKeys(),
      loading: false,
      saving: false,
      /** 못 읽은 이유 — 있으면 폼 대신 이 한 줄을 그린다 */
      unavailable: '',
      /** 저장 실패 문장 */
      errorMessage: '',
      /**
       * 읽기·저장이 **같은 축**(이 패널의 값)을 쓴다 — 사업장을 빨리 바꾸거나 저장 중에 바꾸면 늦게 온 앞 사업장의 응답이
       * 새 사업장의 패널을 덮는다. 나중에 시작한 요청의 응답만 커밋한다(`utils/latestGuard`).
       */
      guard: latestGuard(),
    }
  },

  computed: {
    /** 읽을 수 있는 세션인가 — `permittedRead` 와 **같은 술어**([canRead])다. 없으면 패널도 요청도 없다 */
    canView() {
      return canRead(orderAcceptPolicyRead(this.custCd))
    },
    /** 저장 버튼 — 저장 액션을 가진 세션에만. 볼 수만 있는 세션은 읽기 전용 */
    canSave() {
      return checkAuthActionAccess(ORDER_ACCEPT_POLICY_ACTION.SAVE)
    },
    minutesInput() {
      return minutesInputValue(this.form.minutes)
    },
    /** 대상의 「상위 설정 따름」 — `form.targets === null` 에서 파생한다. 끄면 **빈 목록에서** 시작한다(전체 기본값을 복사하지 않는다) */
    inheritTargets: {
      get() {
        return this.form.targets === null
      },
      set(on) {
        this.form = Object.assign({}, this.form, { targets: on ? null : [] })
      },
    },
    targets: {
      get() {
        return this.form.targets || []
      },
      set(list) {
        this.form = Object.assign({}, this.form, { targets: list })
      },
    },
  },

  watch: {
    custCd: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },

  beforeDestroy() {
    this.guard.close()
  },

  methods: {
    targetLabel,
    targetHint,

    onMinutes(value) {
      this.form = Object.assign({}, this.form, { minutes: minutesFromInput(value) })
    },

    /** 서버 응답(읽기·저장 같은 모양) → 패널 상태. 저장 뒤에도 다시 읽지 않고 이 응답으로 갈아끼운다 */
    apply(body) {
      this.view = orderAcceptPolicyView(body)
      this.form = toPolicyForm(body)
      this.formError = orderAcceptPolicyErrorKeys()
    },

    async load() {
      const token = this.guard.begin()

      this.view = null
      this.form = toPolicyForm(null)
      this.formError = orderAcceptPolicyErrorKeys()
      this.unavailable = ''
      this.errorMessage = ''
      this.saving = false

      if (!this.canView) return

      this.loading = true

      try {
        const outcome = await permittedRead(orderAcceptPolicyRead(this.custCd))

        if (!this.guard.isLatest(token)) return

        if (outcome.allowed) this.apply(outcome.data)
        else this.unavailable = outcome.message || this.$t(this.text.readDenied)
      } catch (error) {
        if (!this.guard.isLatest(token)) return

        // 403 이 아닌 실패(404·500·전송 오류) — 인터셉터가 이미 토스트로 말했다. 패널은 그 자리를 비우지 않고 한 줄 남긴다
        this.unavailable = this.$t(this.text.readFailed)
      } finally {
        if (this.guard.isLatest(token)) this.loading = false
      }
    },

    async save() {
      if (this.saving || !this.canSave) return

      const token = this.guard.begin()

      this.saving = true
      this.formError = orderAcceptPolicyErrorKeys()
      this.errorMessage = ''

      try {
        const response = await this.$axios.put(
          orderAcceptPolicyUrl(this.custCd),
          toPolicyPayload(this.form),
          ORDER_ACCEPT_POLICY_SAVE_CONFIG
        )

        if (!this.guard.isLatest(token)) return

        this.apply(response && response.data)
        this.$message({ type: 'success', message: this.$t(this.text.saved) })
      } catch (error) {
        if (!this.guard.isLatest(token)) return

        // 400 의 필드 오류는 칸에, 그 밖(메시지뿐인 400·404·409)은 패널 아래 한 줄로 — 한 문장을 두 번 말하지 않는다(리뷰 L-5)
        const failure = formFailureOf(error, Object.keys(this.formError), this.$t(this.text.saveFailed))
        mergeErrors(this.formError, failure.fieldErrors)
        this.errorMessage = failure.message
      } finally {
        if (this.guard.isLatest(token)) this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.order-accept-policy {
  margin-top: 12px;
}

.order-accept-policy__header {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a2e);
}

.order-accept-policy__intro {
  margin: 0 0 12px;
}

.order-accept-policy__minutes {
  width: 130px;
}

.order-accept-policy__suffix {
  margin-left: 8px;
  color: #606266;
}

.order-accept-policy__targets {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 4px 16px;
}

.order-accept-policy__targets .el-checkbox {
  margin-right: 0;
}

.order-accept-policy__target-hint {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
}

.order-accept-policy__error {
  margin-bottom: 10px;
}

.order-accept-policy__actions {
  text-align: right;
}
</style>
